import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@/context/UserContext";
import { needsFlexDemo } from "@/shared/constants/clientOnboarding.const";
import ProfileWelcomeCreditsModal from "@/pages/profile-verification/ProfileWelcomeCreditsModal";
import { PROFILE_WELCOME_CREDITS_KEY } from "@/shared/constants/onboarding.const";
import { errorNotification } from "@/shared/utils/errorNotification";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { FLEX_DEMO_MIN_VIEWPORT_MQ, FLEX_DEMO_PATH } from "./flexDemo.const";
import {
  clearFlexDemoSession,
  createFlexDemoSession,
  loadFlexDemoSession,
  saveFlexDemoSession,
} from "./flexDemo.session";
import { FlexDemoSessionContext } from "./FlexDemoSessionContext";
import FlexDemoCoach from "./FlexDemoCoach";
import FlexDemoOptInModal from "./FlexDemoOptInModal";

export function FlexDemoProvider({ children }: { children: React.ReactNode }) {
  const { userDetails, refetchUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = userDetails?.id ?? "";
  const assignedDemo = needsFlexDemo(userDetails);
  /** null until measured — avoids flashing the opt-in on phones. */
  const [isTabletUp, setIsTabletUp] = useState<boolean | null>(null);
  const onFlexDemoPath = location.pathname === FLEX_DEMO_PATH || location.pathname.startsWith(`${FLEX_DEMO_PATH}/`);
  const [session, setSession] = useState(() => loadFlexDemoSession(userId || "anon"));
  const demoEligible = assignedDemo && isTabletUp === true;
  const demoActive =
    demoEligible || (session.replay && onFlexDemoPath && isTabletUp === true);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [leavingDemo, setLeavingDemo] = useState(false);
  const [signupBonusCollaborations, setSignupBonusCollaborations] = useState(0);
  const [signupBonusExpiryMinutes, setSignupBonusExpiryMinutes] = useState(129600);
  const mobileAutoSkipRef = useRef<string | null>(null);

  useEffect(() => {
    const mq = window.matchMedia(FLEX_DEMO_MIN_VIEWPORT_MQ);
    const sync = () => setIsTabletUp(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const persist = useCallback(
    (next: typeof session) => {
      setSession(next);
      if (userId) saveFlexDemoSession(userId, next);
    },
    [userId],
  );

  useEffect(() => {
    if (!userId) return;
    setSession(loadFlexDemoSession(userId));
  }, [userId]);

  useEffect(() => {
    if (!userId || !demoEligible) return;
    const shouldShowWelcome =
      sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY) === "1" && !session.welcomeDismissed;
    if (!shouldShowWelcome) return;
    void trpc.paymentConfig.get
      .query()
      .then((config) => {
        const bonus = config.signupBonusCollaborations ?? 0;
        setSignupBonusCollaborations(bonus);
        setSignupBonusExpiryMinutes(config.signupBonusExpiryMinutes ?? 129600);
        if (bonus <= 0) {
          sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
          persist({ ...session, welcomeDismissed: true });
          return;
        }
        setWelcomeOpen(true);
      })
      .catch(() => {
        sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
        persist({ ...session, welcomeDismissed: true });
      });
  }, [userId, demoEligible, persist, session]);

  const dismissWelcome = useCallback(() => {
    sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
    persist({ ...session, welcomeDismissed: true });
    setWelcomeOpen(false);
  }, [persist, session]);

  const acceptDemo = useCallback(() => {
    persist({ ...session, accepted: true });
  }, [persist, session]);

  const startDemo = useCallback(() => {
    if (!window.matchMedia(FLEX_DEMO_MIN_VIEWPORT_MQ).matches) {
      return;
    }
    persist(
      createFlexDemoSession({
        accepted: true,
        replay: true,
        welcomeDismissed: true,
      }),
    );
    navigate(FLEX_DEMO_PATH);
  }, [navigate, persist]);

  const leaveDemo = useCallback(
    async (title: string) => {
      setLeavingDemo(true);
      try {
        await trpc.company.completeFlexDemo.mutate();
        if (userId) clearFlexDemoSession(userId);
        await refetchUser();
        if (location.pathname === FLEX_DEMO_PATH || location.pathname.startsWith(`${FLEX_DEMO_PATH}/`)) {
          navigate("/my-discovery", { replace: true });
        }
      } catch (err) {
        // Header replay can still exit if the mark-complete call fails.
        if (session.replay && !assignedDemo) {
          if (userId) clearFlexDemoSession(userId);
          navigate("/my-discovery", { replace: true });
          return;
        }
        handleTrpcError(err);
        errorNotification({ title, message: "Please try again." });
      } finally {
        setLeavingDemo(false);
      }
    },
    [assignedDemo, location.pathname, navigate, refetchUser, session.replay, userId],
  );

  const skipDemo = useCallback(() => leaveDemo("Could not skip demo"), [leaveDemo]);
  const cancelDemo = useCallback(() => leaveDemo("Could not cancel demo"), [leaveDemo]);

  // Phones / narrow viewports: mark demo complete silently — no opt-in popup, no walkthrough.
  useEffect(() => {
    if (isTabletUp !== false) {
      if (isTabletUp === true) mobileAutoSkipRef.current = null;
      return;
    }
    if (!assignedDemo || !userId || leavingDemo) return;
    if (mobileAutoSkipRef.current === userId) return;
    mobileAutoSkipRef.current = userId;
    void skipDemo();
  }, [assignedDemo, isTabletUp, leavingDemo, skipDemo, userId]);

  const ctx = useMemo(
    () => ({ session, persist, userId, demoActive, startDemo, cancelDemo, leavingDemo }),
    [session, persist, userId, demoActive, startDemo, cancelDemo, leavingDemo],
  );

  return (
    <FlexDemoSessionContext.Provider value={ctx}>
      {children}
      {demoEligible ? (
        <ProfileWelcomeCreditsModal
          opened={welcomeOpen}
          onClose={dismissWelcome}
          onContinue={dismissWelcome}
          signupBonusCollaborations={signupBonusCollaborations}
          signupBonusExpiryMinutes={signupBonusExpiryMinutes}
          verificationFeeCollaborations={0}
          verificationFeeCharged={false}
        />
      ) : null}
      <FlexDemoOptInModal
        opened={demoEligible && !welcomeOpen && !session.accepted}
        skipping={leavingDemo}
        onYes={acceptDemo}
        onNo={() => void skipDemo()}
      />
      {demoActive && session.accepted && !welcomeOpen ? <FlexDemoCoach /> : null}
    </FlexDemoSessionContext.Provider>
  );
}
